import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createTheme, ThemeProvider as MuiThemeProvider, useTheme } from '@mui/material';
import type { PaletteColor, PaletteColorOptions } from '@mui/material/styles';

import { contrast, getRGB, isSafari } from '@labradorsports/utils';

import { setStatusBarColor } from '../../app/plugins/statusbar.js';
import { Selectors } from '../../store/index.js';

import { useLogger } from './index.js';

declare module '@mui/material/styles' {
    interface Palette {
        brandPrimary?: PaletteColor;
        brandSecondary?: PaletteColor;
    }
    interface PaletteOptions {
        brandPrimary?: PaletteColorOptions;
        brandSecondary?: PaletteColorOptions;
    }
}

const defaultColorPrimary = '#000229';
const defaultColorSecondary = '#ff4f00';
const textColor = 'var(--text-color-1)';

const brandedTheme = (
    colorPrimary: string,
    colorSecondary: string,
    primaryIsLight: boolean,
    secondaryIsLight: boolean
) => {
    return createTheme({
        cssVariables: true,
        typography: {
            body1: {
                color: textColor,
            },
            body2: {
                color: textColor,
            },
        },
        palette: {
            primary: {
                main: defaultColorPrimary,
            },
            secondary: {
                main: colorSecondary,
                contrastText: secondaryIsLight ? textColor : '#fff',
            },
            success: {
                main: '#72bf00',
                contrastText: '#fff',
            },
            brandPrimary: {
                main: colorPrimary,
            },
            brandSecondary: {
                main: colorSecondary,
            },
        },
        components: {
            MuiInputLabel: {
                styleOverrides: {
                    formControl: {
                        textTransform: 'uppercase',
                        '&$focused': {
                            color: '#00bcd4',
                        },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        '&$error': {
                            color: 'rgb(244, 67, 54)',
                        },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&$focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00bcd4',
                        },
                        backgroundColor: 'white',
                    },
                    notchedOutline: {
                        textTransform: 'uppercase',
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: defaultColorPrimary,
                        height: '3px',
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        whiteSpace: 'nowrap',
                        '&.Mui-selected': {
                            color: defaultColorPrimary,
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    textPrimary: {
                        color: '#006DA3',
                    },
                    outlinedPrimary: {
                        color: defaultColorPrimary,
                        borderColor: defaultColorPrimary,
                    },
                    containedPrimary: {
                        backgroundColor: colorPrimary,
                        color: primaryIsLight ? textColor : '#fff',
                        '&$disabled': {
                            color: isSafari() ? '#fff' : undefined,
                        },
                    },
                    textSecondary: {
                        color: secondaryIsLight ? textColor : colorSecondary,
                    },
                    outlinedSecondary: {
                        borderColor: secondaryIsLight ? textColor : colorSecondary,
                        color: secondaryIsLight ? textColor : colorSecondary,
                    },
                    containedSecondary: {
                        border: `1px solid ${secondaryIsLight ? textColor : 'transparent'}`,
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    primary: {
                        backgroundColor: colorPrimary,
                        color: primaryIsLight ? textColor : '#fff',
                        '&$disabled': {
                            color: isSafari() ? '#fff' : undefined,
                        },
                    },
                    secondary: {
                        border: `1px solid ${secondaryIsLight ? textColor : 'transparent'}`,
                    },
                },
            },
            MuiFormControl: {
                styleOverrides: {
                    marginNormal: {
                        marginTop: '8px',
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        minWidth: '0px',
                    },
                },
            },
            MuiDialog: {
                defaultProps: {
                    style: { maxWidth: '100%' },
                },
                styleOverrides: {
                    container: {
                        maxWidth: '100%',
                    },
                },
            },
        },
    });
};

export interface ThemeProviderProps {
    updateTemporaryColors: (colorPrimary?: string, colorSecondary?: string) => void;
}

export const ThemeContext = createContext<ThemeProviderProps>(null);

export const useCustomTheme = () => {
    const { updateTemporaryColors } = useContext(ThemeContext);
    const theme = useTheme();

    return { muiTheme: theme, updateTemporaryColors, defaultColorPrimary, defaultColorSecondary };
};

export const ThemeProvider: FC = ({ children }) => {
    const logger = useLogger();
    const [temporaryPrimary, setTemporaryPrimary] = useState(null);
    const [temporarySecondary, setTemporarySecondary] = useState(null);
    const branding = useSelector(Selectors.currentBranding);
    const { colorPrimary = defaultColorPrimary, colorSecondary = defaultColorSecondary } = branding ?? {};

    const finalColorPrimary = temporaryPrimary ?? colorPrimary;
    const finalColorSecondary = temporarySecondary ?? colorSecondary;
    const primaryIsLight = contrast([255, 255, 255], getRGB(finalColorPrimary)) < 2;
    const secondaryIsLight = contrast([255, 255, 255], getRGB(finalColorSecondary)) < 2;

    const theme = useMemo(
        () => brandedTheme(finalColorPrimary, finalColorSecondary, primaryIsLight, secondaryIsLight),
        [finalColorPrimary, finalColorSecondary, primaryIsLight, secondaryIsLight]
    );

    const updateTemporaryColors = (colorPrimary: string, colorSecondary: string) => {
        setTemporaryPrimary(colorPrimary);
        setTemporarySecondary(colorSecondary);
    };

    useEffect(() => {
        if (APP) {
            setStatusBarColor(logger, finalColorPrimary);
        }
    }, [finalColorPrimary]);

    return (
        <MuiThemeProvider theme={theme}>
            <ThemeContext.Provider value={{ updateTemporaryColors }}>
                <style>{`
                    :root {
                        --theme-color-primary: ${finalColorPrimary};
                        --theme-color-secondary: ${finalColorSecondary};
                        --theme-primary-foreground: ${primaryIsLight ? 'rgba(0, 0, 0, 0.87)' : '#fff'};
                        --theme-secondary-foreground: ${secondaryIsLight ? 'rgba(0, 0, 0, 0.87)' : '#fff'};
                    }
                `}</style>
                {children}
            </ThemeContext.Provider>
        </MuiThemeProvider>
    );
};
